// exclusivePrivateHandler.js
'use strict';
import {
  MERGE_LIVE_STREAM_DATA,
  MERGE_OPERATION_DATA,
  REMOVE_OPERATION_DATA,
} from '../ActionTypes.js';

import getLiveStreamData from '../selector/getLiveStreamData.js';
import getGoalData from '../selector/getGoalData.js';
import { getLiveStreamExclusivePrivatePrice } from '../selector/getLiveStreamPrice.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getOperationData from '../selector/getOperationData.js';
import getExternalData from '../selector/getExternalData.js';
import {
  getIsStreamActivated,
  getIsMeExclusivePrivateGoalInitiator,
  getIsMeExclusivePrivateLastGoalInitiator,
  getIsMeExclusivePrivateInviteInitiator,
} from '../selector/getLivestreamComputedData.js';

import addModal from '../action/addModal.js';
import toggleModal from '../action/toggleModal.js';
import setPrivateStreamAutoPayment from '../action/setPrivateStreamAutoPayment.js';
import setIsPrivateStreamUnlocked from '../action/setIsPrivateStreamUnlocked.js';
import setTriggerPrivateInviteCountdown from '../action/setTriggerPrivateInviteCountdown.js';

import { ExclusivePrivateStatus } from '../resource/liveStreamConstants.js';
import { type as GoalType } from '../resource/goalConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import trackMixpanel from '../resource/mixpanel.js';
import { EventTypes } from '../resource/trackingConstants.js';

/**
 * Helper function to get all required data for tracking
 * @param {Object} params
 * @param {string} params.streamId - The stream ID
 * @param {Object} params.getState - Redux getState function
 * @returns {Object} All required data for tracking
 */
const getTrackingData = ({ streamId, getState }) => {
  const currentGoalId = getLiveStreamData(
    getState(),
    [streamId, 'exclusivePrivate'],
    'currentId'
  );
  const lastGoalId = getLiveStreamData(
    getState(),
    [streamId, 'exclusivePrivate'],
    'lastId'
  );

  const currentGoalType = getGoalData(
    getState(),
    [currentGoalId, 'context'],
    'type'
  );
  const lastGoalType = getGoalData(getState(), [lastGoalId, 'context'], 'type');

  return {
    currentGoalId,
    currentGoalType,
    lastGoalId,
    lastGoalType,
    streamStatus: getLiveStreamData(getState(), [streamId], 'status'),
    fromStatus: getLiveStreamData(
      getState(),
      [streamId, 'exclusivePrivate'],
      'status'
    ),
    activeStreamId: getOperationData(
      getState(),
      ['liveStream'],
      'activeStreamId'
    ),
    clientTimestamp: getExternalData(getState(), ['timestamp'], 'client'),
    serverTimestamp: getExternalData(getState(), ['timestamp'], 'server'),
    price: getLiveStreamExclusivePrivatePrice(getState(), streamId),
    currentGoalExclusiveTo: getGoalData(
      getState(),
      [currentGoalId, 'context'],
      'exclusive_to'
    ),
    lastGoalExclusiveTo: getGoalData(
      getState(),
      [lastGoalId, 'context'],
      'exclusive_to'
    ),
  };
};

/**
 * Helper function to track exclusive private status changes
 * @param {Object} params
 * @param {string} params.streamId - The stream ID
 * @param {string} params.nextStatus - The next status to track
 * @param {Object} params.getState - Redux getState function
 */
const trackExclusiveStatusChange = ({ streamId, nextStatus, getState }) => {
  const trackingData = getTrackingData({ streamId, getState });

  trackMixpanel({
    type: EventTypes.LIVESTREAM_EXCLUSIVE_STATUS_CHANGE,
    payload: {
      streamId,
      ...trackingData,
      nextStatus,
    },
  });
};

/**
 * Exclusive private handler
 * @kind action
 * @param {string} {streamId} - stream id.
 * @return {Promise} Action promise.
 */
const exclusivePrivateHandler =
  ({ streamId } = {}) =>
  async (dispatch, getState) => {
    const status = getLiveStreamData(
      getState(),
      [streamId, 'exclusivePrivate'],
      'status'
    );

    const currentGoalId = getLiveStreamData(
      getState(),
      [streamId, 'exclusivePrivate'],
      'currentId'
    );
    const lastGoalId = getLiveStreamData(
      getState(),
      [streamId, 'exclusivePrivate'],
      'lastId'
    );

    const currentGoalType = getGoalData(
      getState(),
      [currentGoalId, 'context'],
      'type'
    );
    const lastGoalType = getGoalData(
      getState(),
      [lastGoalId, 'context'],
      'type'
    );

    const agreedUnix = getGoalData(getState(), [lastGoalId], 'agreed');
    const isAccepted =
      GoalType.TRIGGER_EXCLUSIVE === lastGoalType && agreedUnix != null;

    const cooldownUnix = getGoalData(getState(), [lastGoalId], 'cooldown');
    const nowUnix = getCurrentUnixTimestamp({
      offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
    });

    const isCooldown =
      GoalType.TRIGGER_EXCLUSIVE === lastGoalType &&
      cooldownUnix != null &&
      nowUnix < cooldownUnix;

    const currentGoalNbf = getGoalData(getState(), [currentGoalId], 'nbf');

    const isPerforming =
      GoalType.EXCLUSIVE === currentGoalType && nowUnix >= currentGoalNbf;
    const isPreparing =
      GoalType.EXCLUSIVE === currentGoalType && nowUnix < currentGoalNbf;

    let nextStatus = undefined;

    switch (true) {
      case isPerforming: {
        nextStatus = ExclusivePrivateStatus.PERFORMING;
        break;
      }
      case isPreparing: {
        nextStatus = ExclusivePrivateStatus.PREPARING;
        break;
      }
      case GoalType.TRIGGER_EXCLUSIVE === currentGoalType:
        nextStatus = ExclusivePrivateStatus.CONSIDERING;
        break;
      case isAccepted:
        nextStatus = ExclusivePrivateStatus.ACCEPTED;
        break;
      case isCooldown:
        nextStatus = ExclusivePrivateStatus.COOLDOWN;
        break;
      default:
        nextStatus = ExclusivePrivateStatus.DEFAULT;
        break;
    }

    if (status !== nextStatus) {
      const leaveHandler = StatusHandlerMap[status]?.onLeave;
      const enterHandler = StatusHandlerMap[nextStatus]?.onEnter;

      if (leaveHandler) {
        await dispatch(leaveHandler({ streamId, nextStatus }));
      }
      if (enterHandler) {
        return dispatch(enterHandler({ streamId }));
      }
    }
  };

export default exclusivePrivateHandler;

/**
 * @typedef StatusHandler
 * @property {function} onEnter - required handler.
 * @property {function} onLeave - optional handler.
 */

/**
 * Exclusive private status handler map
 * @property {StatusHandler}
 */
const StatusHandlerMap = {
  [ExclusivePrivateStatus.DEFAULT]: {
    onEnter:
      ({ streamId } = {}) =>
      async (dispatch, getState) => {
        trackExclusiveStatusChange({
          streamId,
          nextStatus: ExclusivePrivateStatus.DEFAULT,
          getState,
        });
        dispatch({
          type: MERGE_LIVE_STREAM_DATA,
          payload: {
            selectPath: [streamId, 'exclusivePrivate'],
            data: {
              status: ExclusivePrivateStatus.DEFAULT,
              currentId: undefined,
              lastId: undefined,
            },
          },
        });
      },
  },
  [ExclusivePrivateStatus.CONSIDERING]: {
    onEnter:
      ({ streamId } = {}) =>
      async (dispatch, getState) => {
        trackExclusiveStatusChange({
          streamId,
          nextStatus: ExclusivePrivateStatus.CONSIDERING,
          getState,
        });
        dispatch({
          type: MERGE_LIVE_STREAM_DATA,
          payload: {
            selectPath: [streamId, 'exclusivePrivate'],
            data: {
              status: ExclusivePrivateStatus.CONSIDERING,
            },
          },
        });
        // clear the client-side pre-send countdown
        dispatch(setTriggerPrivateInviteCountdown({ streamId, seconds: null }));
      },
  },
  [ExclusivePrivateStatus.COOLDOWN]: {
    onEnter:
      ({ streamId } = {}) =>
      async (dispatch, getState) => {
        const lastGoalId = getLiveStreamData(
          getState(),
          [streamId, 'exclusivePrivate'],
          'lastId'
        );
        trackExclusiveStatusChange({
          streamId,
          nextStatus: ExclusivePrivateStatus.COOLDOWN,
          getState,
        });
        dispatch({
          type: MERGE_LIVE_STREAM_DATA,
          payload: {
            selectPath: [streamId, 'exclusivePrivate'],
            data: {
              status: ExclusivePrivateStatus.COOLDOWN,
            },
          },
        });

        dispatch(setPrivateStreamAutoPayment({ streamId, isEnabled: false }));
        dispatch(
          setIsPrivateStreamUnlocked({
            streamId,
            isPrivateStreamUnlocked: false,
          })
        );

        const isGoalCreator = getIsMeExclusivePrivateLastGoalInitiator(
          getState(),
          streamId
        );

        const isStreamActivated = getIsStreamActivated(getState(), streamId);

        if (isStreamActivated && isGoalCreator) {
          dispatch(
            addModal({
              id: 'TriggerExclusiveNotificationModal',
              payload: {
                titleKey: 'trigger_exclusive_invitation_reject_title',
                descriptionKey:
                  'trigger_exclusive_invitation_reject_description',
                interpolationValues: { min: 5 },
              },
              transitionStatus: 'opening',
            })
          );
        }

        const cooldownUnix = getGoalData(getState(), [lastGoalId], 'cooldown');

        if (cooldownUnix != null) {
          const nowUnix = getCurrentUnixTimestamp({
            offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
          });

          const timerId = setTimeout(
            () => {
              dispatch(exclusivePrivateHandler({ streamId }));
            },
            (cooldownUnix - nowUnix) * 1000
          );
          dispatch({
            type: MERGE_OPERATION_DATA,
            payload: {
              selectPath: [
                'timer',
                'livestream',
                streamId,
                'exclusivePrivateToDefault',
              ],
              data: {
                timerId,
                unixTimestamp: cooldownUnix,
              },
            },
          });
        }
      },
    onLeave:
      ({ streamId } = {}) =>
      async (dispatch, getState) => {
        const selectPath = [
          'timer',
          'livestream',
          streamId,
          'exclusivePrivateToDefault',
        ];
        const timerId = getOperationData(getState(), selectPath, 'timerId');
        if (timerId) {
          clearTimeout(timerId);
          dispatch({
            type: REMOVE_OPERATION_DATA,
            payload: {
              selectPath,
            },
          });
        }
      },
  },
  [ExclusivePrivateStatus.ACCEPTED]: {
    onEnter:
      ({ streamId } = {}) =>
      async (dispatch, getState) => {
        trackExclusiveStatusChange({
          streamId,
          nextStatus: ExclusivePrivateStatus.ACCEPTED,
          getState,
        });
        dispatch({
          type: MERGE_LIVE_STREAM_DATA,
          payload: {
            selectPath: [streamId, 'exclusivePrivate'],
            data: {
              status: ExclusivePrivateStatus.ACCEPTED,
            },
          },
        });

        // At ACCEPTED the trigger goal has moved to lastId; getIsMeExclusivePrivateInviteInitiator
        // reads lastId for the ACCEPTED status, so it resolves to the invited user.
        const isGoalCreator = getIsMeExclusivePrivateInviteInitiator(
          getState(),
          streamId
        );
        const isStreamActivated = getIsStreamActivated(getState(), streamId);

        // Initiator (the invited user): matched successfully.
        // The "creator accepted someone else" notice moved to PREPARING onEnter,
        // since bystanders never reach ACCEPTED (they jump straight to PREPARING).
        if (isStreamActivated && isGoalCreator) {
          dispatch(
            addModal({
              id: 'TriggerExclusiveNotificationModal',
              payload: {
                titleKey: 'trigger_exclusive_succeeded_title',
                descriptionKey: 'trigger_exclusive_succeeded_subtitle',
                interpolationValues: { min: 1 },
              },
              transitionStatus: 'opening',
            })
          );
        }
      },
  },
  [ExclusivePrivateStatus.PERFORMING]: {
    onEnter:
      ({ streamId } = {}) =>
      async (dispatch, getState) => {
        trackExclusiveStatusChange({
          streamId,
          nextStatus: ExclusivePrivateStatus.PERFORMING,
          getState,
        });
        dispatch({
          type: MERGE_LIVE_STREAM_DATA,
          payload: {
            selectPath: [streamId, 'exclusivePrivate'],
            data: {
              status: ExclusivePrivateStatus.PERFORMING,
            },
          },
        });
        dispatch(
          toggleModal({
            modalId: 'LiveStreamTriggerPrivateModal',
            isOpen: false,
          })
        );
      },
    onLeave:
      ({ streamId } = {}) =>
      async dispatch => {
        // Leaving PERFORMING: clear the affordable-watch-time run-out timestamp
        // so stale paid state can't linger. It is otherwise only cleared on a
        // later TOKEN_API refresh, preset switch, or stream reset — none of
        // which is guaranteed to fire when the 1-1 ends.
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['liveStream', 'paid', streamId],
            data: { exclusivePrivateRunOutAt: null },
          },
        });
      },
  },
  [ExclusivePrivateStatus.PREPARING]: {
    onEnter:
      ({ streamId } = {}) =>
      async (dispatch, getState) => {
        const currentGoalId = getLiveStreamData(
          getState(),
          [streamId, 'exclusivePrivate'],
          'currentId'
        );

        trackExclusiveStatusChange({
          streamId,
          nextStatus: ExclusivePrivateStatus.PREPARING,
          getState,
        });
        dispatch({
          type: MERGE_LIVE_STREAM_DATA,
          payload: {
            selectPath: [streamId, 'exclusivePrivate'],
            data: {
              status: ExclusivePrivateStatus.PREPARING,
            },
          },
        });

        const nowUnix = getCurrentUnixTimestamp({
          offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
        });
        const nbf = getGoalData(getState(), [currentGoalId], 'nbf');
        const delayMs = (nbf - nowUnix) * 1000;

        // Bystander (not the invited user): the creator accepted someone else's
        // 1-1, notify that this stream is going private. Gated by the current
        // exclusive goal's `exclusive_to` so the initiator is excluded.
        const isGoalCreator = getIsMeExclusivePrivateGoalInitiator(
          getState(),
          streamId
        );
        const isStreamActivated = getIsStreamActivated(getState(), streamId);
        if (isStreamActivated && !isGoalCreator) {
          // Time left until PREPARING → PERFORMING (nbf), clamped for the
          // race where nbf passes between dispatch and this handler.
          const remainingSeconds = Math.max(0, Math.round(nbf - nowUnix));
          dispatch(
            addModal({
              id: 'TriggerExclusiveNotificationModal',
              payload: {
                titleKey:
                  'trigger_exclusive_invitation_creator_accept_other_request_title',
                descriptionKey:
                  'trigger_exclusive_invitation_creator_accept_other_request_description',
                interpolationValues: {
                  min: Math.floor(remainingSeconds / 60),
                  sec: `${remainingSeconds % 60}`.padStart(2, '0'),
                },
              },
              transitionStatus: 'opening',
            })
          );
          dispatch(
            toggleModal({
              modalId: 'LiveStreamTriggerPrivateModal',
              isOpen: false,
            })
          );
        }

        const timerId = setTimeout(() => {
          trackMixpanel({
            type: EventTypes.LIVESTREAM_EXCLUSIVE_PREPARING_TIMER,
            payload: {
              streamId,
              action: 'TIMER_FIRED',
              nbf,
              firedAtUnix: getCurrentUnixTimestamp({
                offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
              }),
              currentGoalId,
            },
          });
          dispatch(exclusivePrivateHandler({ streamId }));
        }, delayMs);

        trackMixpanel({
          type: EventTypes.LIVESTREAM_EXCLUSIVE_PREPARING_TIMER,
          payload: {
            streamId,
            action: 'TIMER_SET',
            nbf,
            nowUnix,
            delayMs,
            timerId,
            offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
          },
        });

        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: [
              'timer',
              'livestream',
              streamId,
              'exclusivePrivateToPerforming',
            ],
            data: {
              timerId,
              unixTimestamp: nbf,
            },
          },
        });
      },
    onLeave:
      ({ streamId, nextStatus } = {}) =>
      async (dispatch, getState) => {
        const selectPath = [
          'timer',
          'livestream',
          streamId,
          'exclusivePrivateToPerforming',
        ];
        const timerId = getOperationData(getState(), selectPath, 'timerId');
        if (timerId) {
          clearTimeout(timerId);
          const currentGoalId = getLiveStreamData(
            getState(),
            [streamId, 'exclusivePrivate'],
            'currentId'
          );
          const scheduledNbf = getOperationData(
            getState(),
            selectPath,
            'unixTimestamp'
          );
          trackMixpanel({
            type: EventTypes.LIVESTREAM_EXCLUSIVE_PREPARING_TIMER,
            payload: {
              streamId,
              action: 'TIMER_CLEARED',
              timerId,
              nextStatus,
              currentGoalId,
              nbf: scheduledNbf,
              clearedAtUnix: getCurrentUnixTimestamp({
                offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
              }),
            },
          });
          dispatch({
            type: REMOVE_OPERATION_DATA,
            payload: {
              selectPath,
            },
          });
        }
      },
  },
};
